/**
 * Global delivery location handling.
 * Bengaluru stays as the default fallback until the user explicitly changes city.
 * After that, the last selected city is reused for back/restore flows,
 * while fresh deep links can still seed the page via `?city=`.
 *
 * Prod-safe behavior:
 * - Default Bengaluru is never treated as an explicit persisted preference
 * - Pre-opened / auto-open NDD pages do not overwrite persisted user choice
 * - Returning from PLP to NDD preserves Mumbai/Delhi once user has selected it
 */
(function () {
  'use strict';

  if (window.__deliveryLocationBarScriptLoaded) return;
  window.__deliveryLocationBarScriptLoaded = true;

  const QUERY_PARAM_KEY = 'city';
  const DEFAULT_CITY_KEY = 'bengaluru';
  const MOBILE_BREAKPOINT = 768;
  const DROPDOWN_OPEN_CLASS = 'delivery-location-dropdown-open';
  const OVERLAY_VISIBLE_CLASS = 'is-visible';
  const OVERLAY_NO_TRANSITION_CLASS = 'delivery-location-dropdown-overlay--no-transition';
  const SCROLLBAR_COMPENSATION_VAR = '--delivery-location-scrollbar-compensation';

  const STORAGE_KEYS = {
    city: 'deliveryLocationCity',
    cityLegacy: 'selectedCity',
    cityLegacySession: 'deliveryLocationSelectedCity',
    citySelectionPersisted: 'deliveryLocationCitySelectionPersisted',
    autoOpenSessionPrefix: 'deliveryLocationAutoOpenSeen:',
    navigationResolutionSignal: 'deliveryLocationNavigationResolutionSignal'
  };

  // Canonical city keys with accepted URL/tag aliases.
  const CITY_CONFIG = {
    bengaluru: {
      label: 'Bengaluru',
      queryValues: ['bengaluru', 'blr', 'bangalore'],
      backendTagPattern: /^backend_ndd_blr(?:_[a-z0-9][a-z0-9_-]*)?$/i
    },
    mumbai: {
      label: 'Mumbai',
      queryValues: ['mumbai'],
      backendTagPattern: /^backend_ndd_mum(?:_[a-z0-9][a-z0-9_-]*)?$/i
    },
    delhi: {
      label: 'Delhi',
      queryValues: ['delhi'],
      backendTagPattern: /^backend_ndd_del(?:_[a-z0-9][a-z0-9_-]*)?$/i
    },
    other: {
      label: 'Other',
      queryValues: ['other'],
      backendTagPattern: null
    }
  };

  const state = {
    activeCityKey: DEFAULT_CITY_KEY,
    badgeObserver: null,
    badgeObserverTarget: null,
    badgeRefreshScheduled: false,
    overlayElement: null,
    badgeCutoffTimerId: null,
    overlayDismissBound: false
  };

  const CITY_KEYS = Object.keys(CITY_CONFIG);
  const CITY_LOOKUP = createCityLookup();

  function getDisabledCityKeys(bar) {
    if (!bar || !bar.getAttribute) return [];

    return String(bar.getAttribute('data-disabled-city-keys') || '')
      .split(',')
      .map(function (value) {
        return normalizeCityKey(value);
      })
      .filter(Boolean);
  }

  function getDisabledCityLookup(bar) {
    return getDisabledCityKeys(bar).reduce(function (lookup, cityKey) {
      lookup[cityKey] = true;
      return lookup;
    }, {});
  }

  function getFirstEnabledCityKey(bar) {
    const disabledCityLookup = getDisabledCityLookup(bar);
    const enabledCityKey = CITY_KEYS.find(function (cityKey) {
      return !disabledCityLookup[cityKey];
    });

    return enabledCityKey || DEFAULT_CITY_KEY;
  }

  function resolveCityKeyForBar(cityKey, bar) {
    const normalizedCityKey = normalizeCityKey(cityKey) || DEFAULT_CITY_KEY;
    const disabledCityLookup = getDisabledCityLookup(bar);

    if (disabledCityLookup[normalizedCityKey]) {
      return getFirstEnabledCityKey(bar);
    }

    return normalizedCityKey;
  }

  function getPrimaryDeliveryBar(root) {
    const context = root && root.querySelector ? root : document;
    return context.querySelector('[data-delivery-location-bar]');
  }

  function resolveActiveCityKey(cityKey, bar) {
    const referenceBar = bar || getPrimaryDeliveryBar(document);
    return resolveCityKeyForBar(cityKey, referenceBar);
  }

  function createCityLookup() {
    const lookup = {};

    CITY_KEYS.forEach(function (cityKey) {
      const cityConfig = CITY_CONFIG[cityKey];

      lookup[cityKey] = cityKey;
      lookup[cityConfig.label.toLowerCase()] = cityKey;

      cityConfig.queryValues.forEach(function (queryValue) {
        lookup[queryValue] = cityKey;
      });
    });

    return lookup;
  }

  function getStorageValue(storageType, key) {
    try {
      return window[storageType].getItem(key);
    } catch (error) {
      return null;
    }
  }

  function getRelativeUrl(url) {
    try {
      const resolvedUrl = new URL(url || window.location.href, window.location.href);
      return resolvedUrl.pathname + resolvedUrl.search + resolvedUrl.hash;
    } catch (error) {
      return window.location.pathname + window.location.search + window.location.hash;
    }
  }

  function isBackForwardNavigation() {
    try {
      const navigationEntries = window.performance && window.performance.getEntriesByType
        ? window.performance.getEntriesByType('navigation')
        : [];
      const navigationEntry = navigationEntries && navigationEntries[0];

      if (navigationEntry && navigationEntry.type === 'back_forward') {
        return true;
      }
    } catch (error) {
      // no-op
    }

    try {
      if (
        window.performance &&
        window.performance.navigation &&
        window.performance.navigation.type === 2
      ) {
        return true;
      }
    } catch (error) {
      // no-op
    }

    return false;
  }

  function consumeNavigationResolutionSignal() {
    const rawSignal = getStorageValue('sessionStorage', STORAGE_KEYS.navigationResolutionSignal);
    if (!rawSignal) return null;

    try {
      const parsedSignal = JSON.parse(rawSignal);
      const currentRelativeUrl = getRelativeUrl(window.location.href);
      const expiresAt = Number(parsedSignal && parsedSignal.expiresAt);
      const targetUrl = String((parsedSignal && parsedSignal.targetUrl) || '').trim();
      const strategy = String((parsedSignal && parsedSignal.strategy) || '').trim();

      removeStorageValue('sessionStorage', STORAGE_KEYS.navigationResolutionSignal);

      if (!targetUrl || !strategy) return null;
      if (Number.isFinite(expiresAt) && Date.now() > expiresAt) return null;
      if (targetUrl !== currentRelativeUrl) return null;

      return {
        strategy: strategy,
        targetUrl: targetUrl
      };
    } catch (error) {
      removeStorageValue('sessionStorage', STORAGE_KEYS.navigationResolutionSignal);
      return null;
    }
  }

  function shouldPreferStoredCityOnBoot() {
    const navigationSignal = consumeNavigationResolutionSignal();
    if (navigationSignal && navigationSignal.strategy === 'preferStored') {
      return true;
    }

    return isBackForwardNavigation();
  }

  function setStorageValue(storageType, key, value) {
    try {
      window[storageType].setItem(key, value);
    } catch (error) {
      // no-op when storage is blocked
    }
  }

  function removeStorageValue(storageType, key) {
    try {
      window[storageType].removeItem(key);
    } catch (error) {
      // no-op when storage is blocked
    }
  }

  function getAutoOpenSessionStorageKey(bar) {
    const sessionScope =
      (bar && bar.getAttribute && bar.getAttribute('data-auto-open-session-key')) ||
      window.location.pathname ||
      'global';

    return STORAGE_KEYS.autoOpenSessionPrefix + String(sessionScope).trim();
  }

  function hasAutoOpenedInSession(bar) {
    return getStorageValue('sessionStorage', getAutoOpenSessionStorageKey(bar)) === 'true';
  }

  function markAutoOpenedInSession(bar) {
    setStorageValue('sessionStorage', getAutoOpenSessionStorageKey(bar), 'true');
  }

  function normalizeCityKey(value) {
    if (!value) return null;

    const normalizedValue = String(value).trim().toLowerCase();
    return CITY_LOOKUP[normalizedValue] || null;
  }

  function getCityLabel(cityKey) {
    return CITY_CONFIG[cityKey] ? CITY_CONFIG[cityKey].label : CITY_CONFIG[DEFAULT_CITY_KEY].label;
  }

  function readCityFromUrl() {
    try {
      const urlCity = new URLSearchParams(window.location.search).get(QUERY_PARAM_KEY);
      return normalizeCityKey(urlCity);
    } catch (error) {
      return null;
    }
  }

  function readCityFromStorage() {
    return normalizeCityKey(
      getStorageValue('localStorage', STORAGE_KEYS.city) ||
        getStorageValue('localStorage', STORAGE_KEYS.cityLegacy) ||
        getStorageValue('sessionStorage', STORAGE_KEYS.cityLegacySession)
    );
  }

  function hasStoredUserCitySelection() {
    if (getStorageValue('localStorage', STORAGE_KEYS.citySelectionPersisted) === 'true') {
      return true;
    }

    // Backward compatibility:
    // If older versions already persisted a non-default city,
    // treat it as a valid explicit user choice.
    const storedCity = readCityFromStorage();
    return Boolean(storedCity && storedCity !== DEFAULT_CITY_KEY && storedCity !== 'other');
  }

  function markStoredUserCitySelection(isPersisted) {
    if (isPersisted) {
      setStorageValue('localStorage', STORAGE_KEYS.citySelectionPersisted, 'true');
      return;
    }

    removeStorageValue('localStorage', STORAGE_KEYS.citySelectionPersisted);
  }

  function getStoredOrDefaultCity() {
    return readCityFromStorage() || DEFAULT_CITY_KEY;
  }

  function showOtherCityToast(message) {
    const toastMessage =
      String(message || '').trim() ||
      'Next day delivery is currently available only in Bengaluru, Mumbai, and Delhi. We are expanding to more cities soon.';

    window.csToast?.showToast?.('warning', toastMessage);
  }

  function updateUrl(cityKey) {
    if (!window.history || typeof window.history.replaceState !== 'function') return;

    try {
      const url = new URL(window.location.href);

      if (cityKey) {
        url.searchParams.set(QUERY_PARAM_KEY, cityKey);
      } else {
        url.searchParams.delete(QUERY_PARAM_KEY);
      }

      const queryString = url.searchParams.toString();
      const nextUrl = url.pathname + (queryString ? '?' + queryString : '') + url.hash;

      window.history.replaceState({}, document.title, nextUrl);
    } catch (error) {
      // no-op
    }
  }

  function syncStorage(cityKey, source) {
    // Persist only on explicit user selection,
    // or when restoring an already-known persisted selection.
    const shouldPersist =
      source === 'user' ||
      ((source === 'restore' || source === 'initial') && hasStoredUserCitySelection());

    if (!shouldPersist) {
      return;
    }

    const cityLabel = getCityLabel(cityKey);
    setStorageValue('localStorage', STORAGE_KEYS.city, cityKey);
    setStorageValue('localStorage', STORAGE_KEYS.cityLegacy, cityLabel);
    removeStorageValue('sessionStorage', STORAGE_KEYS.cityLegacySession);
    markStoredUserCitySelection(true);
  }

  function updateSelectedCityUI(cityKey) {
    const bars = document.querySelectorAll('[data-delivery-location-bar]');
    const syncBarUI =
      typeof window.__deliveryLocationBarSyncBarUI === 'function'
        ? window.__deliveryLocationBarSyncBarUI
        : null;

    bars.forEach(function (bar) {
      const resolvedCityKey = resolveCityKeyForBar(cityKey, bar);
      const cityLabel = getCityLabel(resolvedCityKey);

      if (syncBarUI) {
        syncBarUI(bar, resolvedCityKey, cityLabel);
        return;
      }

      const selectedCityElement = bar.querySelector('[data-selected-city]');
      const cityItems = bar.querySelectorAll('[data-city-key]');
      const selectedCityIcon = bar.querySelector('[data-selected-city-icon]');
      const currentCityKey = bar.getAttribute('data-delivery-location-current-city-key');
      const currentIconCityKey =
        selectedCityIcon && selectedCityIcon.getAttribute('data-selected-city-icon-city-key');
      const activeCityItem = bar.querySelector('[data-city-key="' + resolvedCityKey + '"]');

      if (selectedCityElement && selectedCityElement.textContent !== cityLabel) {
        selectedCityElement.textContent = cityLabel;
      }

      if (currentCityKey !== resolvedCityKey) {
        cityItems.forEach(function (item) {
          if (item === activeCityItem) {
            item.setAttribute('data-selected', 'true');
          } else {
            item.removeAttribute('data-selected');
          }
        });
      }

      if (selectedCityIcon && currentIconCityKey !== resolvedCityKey) {
        const activeCityItemIcon =
          activeCityItem && activeCityItem.querySelector('.delivery-location-dropdown__item-icon');

        if (activeCityItemIcon) {
          const nextIcon = activeCityItemIcon.cloneNode(true);
          nextIcon.classList.remove('delivery-location-dropdown__item-icon');
          nextIcon.classList.add('delivery-location-bar__icon');
          nextIcon.setAttribute('data-selected-city-icon', '');
          nextIcon.setAttribute('data-selected-city-icon-city-key', resolvedCityKey);
          selectedCityIcon.replaceWith(nextIcon);
        }
      }

      bar.setAttribute('data-delivery-location-current-city-key', resolvedCityKey);
    });
  }

  function setScrollLock(isLocked) {
    if (!document.documentElement || !document.body) return;

    document.documentElement.classList.toggle(DROPDOWN_OPEN_CLASS, isLocked);
    document.body.classList.toggle(DROPDOWN_OPEN_CLASS, isLocked);

    if (isLocked) {
      const scrollbarWidth = Math.max(window.innerWidth - document.documentElement.clientWidth, 0);
      if (scrollbarWidth > 0) {
        document.body.style.setProperty(SCROLLBAR_COMPENSATION_VAR, scrollbarWidth + 'px');
      } else {
        document.body.style.removeProperty(SCROLLBAR_COMPENSATION_VAR);
      }
      return;
    }

    document.body.style.removeProperty(SCROLLBAR_COMPENSATION_VAR);
  }

  function bindOverlayDismiss(overlay) {
    if (!overlay || state.overlayDismissBound) return;

    overlay.addEventListener('click', function () {
      closeAllDropdowns();
    });
    state.overlayDismissBound = true;
  }

  function ensureOverlayElement() {
    if (!document.body) return null;

    if (state.overlayElement && document.body.contains(state.overlayElement)) {
      bindOverlayDismiss(state.overlayElement);
      return state.overlayElement;
    }

    const existingOverlay = document.querySelector('[data-delivery-location-overlay]');
    if (existingOverlay) {
      state.overlayElement = existingOverlay;
      bindOverlayDismiss(state.overlayElement);
      return state.overlayElement;
    }

    const overlay = document.createElement('div');
    overlay.className = 'delivery-location-dropdown-overlay';
    if (hasOpenDropdown()) {
      overlay.classList.add(OVERLAY_NO_TRANSITION_CLASS);
    }
    overlay.setAttribute('data-delivery-location-overlay', '');
    overlay.setAttribute('aria-hidden', 'true');
    bindOverlayDismiss(overlay);

    document.body.appendChild(overlay);
    state.overlayElement = overlay;
    return state.overlayElement;
  }

  function hasOpenDropdown() {
    return Boolean(
      document.querySelector('[data-delivery-location-bar] [data-dropdown-toggle][aria-expanded="true"]')
    );
  }

  function shouldUseOverlay() {
    return window.matchMedia('(max-width: ' + MOBILE_BREAKPOINT + 'px)').matches;
  }

  function getOverlayTopOffset() {
    let overlayTop = 0;
    const openToggles = document.querySelectorAll(
      '[data-delivery-location-bar] [data-dropdown-toggle][aria-expanded="true"]'
    );

    openToggles.forEach(function (toggleButton) {
      const bar = toggleButton.closest('[data-delivery-location-bar]');
      if (!bar) return;

      const barBottom = Math.ceil(bar.getBoundingClientRect().bottom);
      if (barBottom > overlayTop) {
        overlayTop = barBottom;
      }
    });

    return Math.max(overlayTop, 0);
  }

  function syncDropdownOpenState() {
    const isOpen = hasOpenDropdown();
    const useOverlay = shouldUseOverlay();
    const overlay = useOverlay ? ensureOverlayElement() : state.overlayElement;

    if (overlay) {
      if (useOverlay && isOpen) {
        overlay.style.top = getOverlayTopOffset() + 'px';
      } else {
        overlay.style.removeProperty('top');
      }

      overlay.classList.toggle(OVERLAY_VISIBLE_CLASS, useOverlay && isOpen);
      overlay.setAttribute('aria-hidden', String(!(useOverlay && isOpen)));

      if (overlay.classList.contains(OVERLAY_NO_TRANSITION_CLASS)) {
        window.requestAnimationFrame(function () {
          overlay.classList.remove(OVERLAY_NO_TRANSITION_CLASS);
        });
      }
    }

    setScrollLock(useOverlay && isOpen);
  }

  function setDropdownState(bar, isOpen) {
    if (!bar) return;

    const toggleButton = bar.querySelector('[data-dropdown-toggle]');
    const dropdown = bar.querySelector('[data-city-dropdown]');
    if (!toggleButton || !dropdown) return;

    const measuredHeight = Math.max(dropdown.scrollHeight + 10, 1);
    dropdown.style.setProperty('--delivery-dropdown-max-height', measuredHeight + 'px');

    toggleButton.setAttribute('aria-expanded', String(isOpen));
    dropdown.classList.toggle('show', isOpen);
    dropdown.setAttribute('aria-hidden', String(!isOpen));
    syncDropdownOpenState();
  }

  function scrollPageToTop() {
    if (!window || typeof window.scrollTo !== 'function') return;

    try {
      window.scrollTo(0, 0);
    } catch (error) {
      // no-op
    }
  }

  function closeAllDropdowns() {
    const bars = document.querySelectorAll('[data-delivery-location-bar]');
    bars.forEach(function (bar) {
      setDropdownState(bar, false);
    });
  }

  function cityMatchesTags(cityKey, tagList) {
    const cityConfig = CITY_CONFIG[cityKey];
    if (!tagList || !cityConfig || !cityConfig.backendTagPattern) return false;

    const normalizedTags = tagList
      .split(',')
      .map(function (tag) {
        return String(tag).trim().toLowerCase();
      })
      .filter(Boolean);

    if (normalizedTags.length === 0) return false;

    return normalizedTags.some(function (tag) {
      return cityConfig.backendTagPattern.test(tag);
    });
  }

  function parseCutoffTime(value) {
    if (!value) return null;

    const normalizedValue = String(value).trim().toLowerCase();
    if (!normalizedValue) return null;

    const match = normalizedValue.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
    if (!match) return null;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2] || '0', 10);
    const meridiem = match[3];

    if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59) {
      return null;
    }

    if (meridiem) {
      if (hours < 1 || hours > 12) return null;
      if (hours === 12) {
        hours = 0;
      }
      if (meridiem === 'pm') {
        hours += 12;
      }
    } else if (hours > 23) {
      return null;
    }

    return {
      hours: hours,
      minutes: minutes
    };
  }

  function isPastCutoff(cutoffTimeConfig) {
    if (!cutoffTimeConfig) return false;

    const now = new Date();
    const cutoffTime = new Date(now.getTime());
    cutoffTime.setHours(cutoffTimeConfig.hours, cutoffTimeConfig.minutes, 0, 0);
    return now.getTime() >= cutoffTime.getTime();
  }

  function getBadgeText(badge) {
    if (!badge) return '';

    const beforeCutoffText = String(badge.getAttribute('data-ndd-before-cutoff-text') || '').trim();
    const afterCutoffText = String(badge.getAttribute('data-ndd-after-cutoff-text') || '').trim();

    if (!beforeCutoffText && !afterCutoffText) {
      return '';
    }

    const cutoffTimeConfig = parseCutoffTime(badge.getAttribute('data-ndd-cutoff-time'));
    const afterCutoff = isPastCutoff(cutoffTimeConfig);

    if (afterCutoff) {
      return afterCutoffText || beforeCutoffText;
    }

    return beforeCutoffText || afterCutoffText;
  }

  function stripBadgeTextMarkup(text) {
    return String(text || '').replace(/\*\*(.*?)\*\*/g, '$1');
  }

  function getBadgeTextParts(text) {
    const sourceText = String(text || '');
    const parts = [];
    const emphasisPattern = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match = emphasisPattern.exec(sourceText);

    while (match) {
      if (match.index > lastIndex) {
        parts.push({
          text: sourceText.slice(lastIndex, match.index),
          emphasized: false
        });
      }

      if (match[1]) {
        parts.push({
          text: match[1],
          emphasized: true
        });
      }

      lastIndex = emphasisPattern.lastIndex;
      match = emphasisPattern.exec(sourceText);
    }

    if (lastIndex < sourceText.length) {
      parts.push({
        text: sourceText.slice(lastIndex),
        emphasized: false
      });
    }

    if (parts.length === 0 && sourceText) {
      parts.push({
        text: sourceText,
        emphasized: false
      });
    }

    return parts.filter(function (part) {
      return part.text;
    });
  }

  function renderBadgeText(target, text) {
    if (!target) return;

    const sourceText = String(text || '');
    if (target.getAttribute('data-rendered-badge-text') === sourceText) return;

    const fragments = getBadgeTextParts(sourceText);
    // replaceChildren is unavailable in some embedded/legacy browsers.
    target.textContent = '';

    fragments.forEach(function (fragment) {
      if (fragment.emphasized) {
        const emphasis = document.createElement('span');
        emphasis.className = 'product-block__ndd-badge-emphasis';
        emphasis.textContent = fragment.text;
        target.appendChild(emphasis);
        return;
      }

      target.appendChild(document.createTextNode(fragment.text));
    });

    target.setAttribute('data-rendered-badge-text', sourceText);
  }

  function clearBadgeCutoffTimer() {
    if (!state.badgeCutoffTimerId) return;

    window.clearTimeout(state.badgeCutoffTimerId);
    state.badgeCutoffTimerId = null;
  }

  function scheduleBadgeCutoffRefresh(context) {
    clearBadgeCutoffTimer();

    const badge =
      (context && context.querySelector && context.querySelector('[data-ndd-availability]')) ||
      document.querySelector('[data-ndd-availability]');

    if (!badge) return;

    const cutoffTimeConfig = parseCutoffTime(badge.getAttribute('data-ndd-cutoff-time'));
    if (!cutoffTimeConfig) return;

    const now = new Date();
    const nextRefreshAt = new Date(now.getTime());
    nextRefreshAt.setHours(cutoffTimeConfig.hours, cutoffTimeConfig.minutes, 0, 0);

    if (nextRefreshAt.getTime() <= now.getTime()) {
      nextRefreshAt.setDate(nextRefreshAt.getDate() + 1);
    }

    const refreshDelay = Math.max(nextRefreshAt.getTime() - now.getTime() + 1000, 1000);
    state.badgeCutoffTimerId = window.setTimeout(function () {
      updateCollectionBadges(state.activeCityKey, document);
    }, refreshDelay);
  }

  function updateCollectionBadges(cityKey, root) {
    const context = root && root.querySelectorAll ? root : document;
    const badges = context.querySelectorAll('[data-ndd-availability]');

    badges.forEach(function (badge) {
      const badgeText = getBadgeText(badge);
      const isVisible = Boolean(badgeText) && cityMatchesTags(cityKey, badge.getAttribute('data-ndd-tags'));
      const nextText = isVisible ? badgeText : '';
      const badgeTextElement = badge.querySelector('[data-ndd-text]');

      if (badgeTextElement) {
        renderBadgeText(badgeTextElement, nextText);
      } else if (badge.textContent !== stripBadgeTextMarkup(nextText)) {
        badge.textContent = stripBadgeTextMarkup(nextText);
      }

      if (badge.hidden === isVisible) {
        badge.hidden = !isVisible;
      }

      if (badge.classList.contains('is-visible') !== isVisible) {
        badge.classList.toggle('is-visible', isVisible);
      }
    });

    scheduleBadgeCutoffRefresh(context);
  }

  function scheduleCollectionBadgeRefresh() {
    if (state.badgeRefreshScheduled) return;
    state.badgeRefreshScheduled = true;

    window.requestAnimationFrame(function () {
      state.badgeRefreshScheduled = false;
      updateCollectionBadges(state.activeCityKey, document);
    });
  }

  function ensureCollectionObserver() {
    if (!document.body.classList.contains('template-collection')) return;

    const observerTarget =
      document.querySelector('.collection-listing[data-ajax-container]') ||
      document.querySelector('.product-grid');

    if (!observerTarget) return;
    if (state.badgeObserver && state.badgeObserverTarget === observerTarget) return;

    if (state.badgeObserver) {
      state.badgeObserver.disconnect();
    }

    state.badgeObserverTarget = observerTarget;
    state.badgeObserver = new MutationObserver(function () {
      scheduleCollectionBadgeRefresh();
    });

    state.badgeObserver.observe(observerTarget, {
      childList: true,
      subtree: true
    });
  }

  function emitCityChange(cityKey, source) {
    document.dispatchEvent(
      new CustomEvent('delivery:city-changed', {
        detail: {
          city: cityKey,
          cityLabel: getCityLabel(cityKey),
          source: source || 'system'
        }
      })
    );
  }

  function applyCity(cityKey, source, options) {
    const config = options || {};
    const resolvedCityKey = resolveActiveCityKey(cityKey, getPrimaryDeliveryBar(document));
    const shouldSyncStorageValue = config.syncStorage !== false;
    const shouldUpdateUrlValue = config.updateUrl !== false;

    state.activeCityKey = resolvedCityKey;

    if (shouldSyncStorageValue) {
      syncStorage(resolvedCityKey, source);
    }

    if (shouldUpdateUrlValue) {
      updateUrl(resolvedCityKey);
    }

    updateSelectedCityUI(resolvedCityKey);
    updateCollectionBadges(resolvedCityKey, document);
    emitCityChange(resolvedCityKey, source);

    if (source === 'user' && window.CultStoreSentry) {
      window.CultStoreSentry.addBreadcrumb('Delivery city selected', 'user_action', {
        city: resolvedCityKey,
        page: window.location.pathname,
        source: source || 'system'
      });
    }
  }

  function initDeliveryLocationBar(root) {
    if (!root || !root.querySelectorAll) return;

    const deliveryBars = root.querySelectorAll('[data-delivery-location-bar]');
    if (deliveryBars.length === 0) return;

    deliveryBars.forEach(function (deliveryBar) {
      if (deliveryBar.dataset.deliveryLocationInit === 'true') return;
      deliveryBar.dataset.deliveryLocationInit = 'true';

      const toggleButton = deliveryBar.querySelector('[data-dropdown-toggle]');
      const cityItems = deliveryBar.querySelectorAll('[data-city-key]');
      const shouldStartOpen =
        toggleButton && toggleButton.getAttribute('aria-expanded') === 'true';

      if (toggleButton) {
        toggleButton.addEventListener('click', function () {
          const isOpen = toggleButton.getAttribute('aria-expanded') === 'true';
          if (!isOpen) {
            closeAllDropdowns();
          }
          setDropdownState(deliveryBar, !isOpen);
        });
      }

      cityItems.forEach(function (item) {
        item.addEventListener('click', function (event) {
          event.preventDefault();

          const cityKey = item.getAttribute('data-city-key');
          const otherCityToastMessage = String(
            item.getAttribute('data-other-city-toast-message') || ''
          ).trim();

          if (cityKey === 'other') {
            setDropdownState(deliveryBar, false);
            applyCity(cityKey, 'user', {
              syncStorage: true,
              updateUrl: true
            });
            showOtherCityToast(otherCityToastMessage);
            return;
          }

          applyCity(cityKey, 'user', {
            syncStorage: true,
            updateUrl: true
          });
          setDropdownState(deliveryBar, false);
        });
      });

      setDropdownState(deliveryBar, Boolean(shouldStartOpen));
    });
  }

  function autoOpenDropdownOnLoad() {
    const autoOpenBar = document.querySelector(
      '[data-delivery-location-bar][data-auto-open-on-load="true"]'
    );

    if (!autoOpenBar) return;

    if (autoOpenBar.getAttribute('data-auto-open-preopened') === 'true') {
      syncDropdownOpenState();
      return;
    }

    if (hasAutoOpenedInSession(autoOpenBar)) return;

    markAutoOpenedInSession(autoOpenBar);
    window.requestAnimationFrame(function () {
      scrollPageToTop();

      const toggleButton = autoOpenBar.querySelector('[data-dropdown-toggle]');
      if (toggleButton && toggleButton.getAttribute('aria-expanded') === 'true') {
        syncDropdownOpenState();
      } else {
        setDropdownState(autoOpenBar, true);
      }

      window.requestAnimationFrame(scrollPageToTop);
    });
  }

  function refreshDeliveryLocationBar(root) {
    const context = root && root.querySelectorAll ? root : document;
    initDeliveryLocationBar(context);
    updateSelectedCityUI(state.activeCityKey);
    ensureCollectionObserver();
    scheduleCollectionBadgeRefresh();
    syncDropdownOpenState();
  }

  function getInitialCityState(isPreOpenedExternalBar, options) {
    const config = options || {};
    const preferStoredSelection = config.preferStoredSelection === true;
    const hasPersistedSelection = hasStoredUserCitySelection();
    const storedCity = getStoredOrDefaultCity();
    const urlCity = readCityFromUrl();
    const primaryBar = getPrimaryDeliveryBar(document);
    const resolveInitialCityKey = function (cityKey) {
      return resolveCityKeyForBar(cityKey, primaryBar);
    };

    // Back/restore flows should preserve the user's explicit city choice even if
    // an older in-session URL still carries a stale city query param.
    if (preferStoredSelection && hasPersistedSelection) {
      const resolvedStoredCity = resolveInitialCityKey(storedCity);
      return {
        cityKey: resolvedStoredCity,
        shouldSyncStorage: false,
        shouldUpdateUrl: true
      };
    }

    // Fresh entries and deep links should still honor a valid incoming query param,
    // including `other`.
    if (urlCity) {
      const resolvedUrlCity = resolveInitialCityKey(urlCity);
      return {
        cityKey: resolvedUrlCity,
        shouldSyncStorage: false,
        shouldUpdateUrl: true
      };
    }

    if (hasPersistedSelection) {
      const resolvedStoredCity = resolveInitialCityKey(storedCity);
      return {
        cityKey: resolvedStoredCity,
        shouldSyncStorage: false,
        shouldUpdateUrl: true
      };
    }

    // For special pre-opened external bar pages without persisted user choice,
    // show Bengaluru as visual default only. Do not persist or canonicalize via storage.
    if (isPreOpenedExternalBar) {
      return {
        cityKey: resolveInitialCityKey(DEFAULT_CITY_KEY),
        shouldSyncStorage: false,
        shouldUpdateUrl: false
      };
    }

    // Neutral default case.
    return {
      cityKey: resolveInitialCityKey(DEFAULT_CITY_KEY),
      shouldSyncStorage: false,
      shouldUpdateUrl: false
    };
  }

  function restorePersistedCity(source) {
    const autoOpenOnLoadBar = document.querySelector(
      '[data-delivery-location-bar][data-auto-open-on-load="true"]'
    );
    const isPreOpenedExternalBar = Boolean(
      autoOpenOnLoadBar && autoOpenOnLoadBar.getAttribute('data-auto-open-preopened') === 'true'
    );
    const resolvedCityState = getInitialCityState(isPreOpenedExternalBar, {
      preferStoredSelection: true
    });

    applyCity(resolvedCityState.cityKey, source || 'restore', {
      syncStorage: false,
      updateUrl: resolvedCityState.shouldUpdateUrl
    });
  }

  function boot() {
    const autoOpenOnLoadBar = document.querySelector(
      '[data-delivery-location-bar][data-auto-open-on-load="true"]'
    );
    const isPreOpenedExternalBar = Boolean(
      autoOpenOnLoadBar && autoOpenOnLoadBar.getAttribute('data-auto-open-preopened') === 'true'
    );
    const initialCityState = getInitialCityState(isPreOpenedExternalBar, {
      preferStoredSelection: shouldPreferStoredCityOnBoot()
    });

    initDeliveryLocationBar(document);
    ensureOverlayElement();

    applyCity(initialCityState.cityKey, 'initial', {
      syncStorage: initialCityState.shouldSyncStorage,
      updateUrl: initialCityState.shouldUpdateUrl
    });

    ensureCollectionObserver();
    syncDropdownOpenState();
    autoOpenDropdownOnLoad();

    document.addEventListener('click', function (event) {
      const withinBar =
        event.target && event.target.closest && event.target.closest('[data-delivery-location-bar]');
      if (!withinBar) {
        closeAllDropdowns();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeAllDropdowns();
      }
    });

    window.addEventListener('resize', function () {
      if (hasOpenDropdown()) {
        syncDropdownOpenState();
      }
    });

    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      restorePersistedCity('restore');
    });

    document.addEventListener('shopify:section:load', function (event) {
      refreshDeliveryLocationBar(event.target);
    });

    document.addEventListener('collection:filters-refreshed', function (event) {
      const refreshedRoot = event.detail && event.detail.root ? event.detail.root : document;
      refreshDeliveryLocationBar(refreshedRoot);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
